import { deploymentConfig } from "../deployment-config";
import { session } from "../auth/session";
import { ApiError, createApiClient } from "@jkast90/kast-common/api-client";
import { DEFAULT_TIMEOUT_MS } from "@jkast90/kast-common/fetch";

export interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
}

/** How long a JSON call may take before it counts as the server being unreachable. */
export const REQUEST_TIMEOUT_MS = DEFAULT_TIMEOUT_MS;
/** How long a file upload (a presigned S3 PUT) may take. */
export const UPLOAD_TIMEOUT_MS = 60_000;

/** The client's retry numbers; api.test.ts reads them. */
export const UI_CONSTANTS = {
  MAX_RETRIES: 2,
  RETRY_DELAY_MS: 400,
  RETRY_BACKOFF_MULTIPLIER: 2,
} as const;

export const UNREACHABLE = "Server unreachable: the request timed out";

/**
 * Methods that are safe to send again after a timeout, network error or 5xx
 * (standard 11). A POST is never retried: the first attempt may have reached
 * the server, and POST /shop/orders would place the order twice.
 */
const RETRYABLE_METHODS = new Set(["GET", "HEAD"]);

export function isRetryableMethod(method: string): boolean {
  return RETRYABLE_METHODS.has(method.toUpperCase());
}

const client = createApiClient({
  baseUrl: () => deploymentConfig.get("apiUrl"),
  session,
  timeoutMs: REQUEST_TIMEOUT_MS,
  retry: {
    retries: UI_CONSTANTS.MAX_RETRIES,
    delayMs: UI_CONSTANTS.RETRY_DELAY_MS,
    backoff: UI_CONSTANTS.RETRY_BACKOFF_MULTIPLIER,
  },
  // The API log is the fetch recorder's (main.tsx installs it).
  recordCalls: false,
  // A network error reaches the caller as the browser raised it.
  onNetworkFailure: (error) => error as Error,
  messages: { timeout: UNREACHABLE, expired: "Authentication expired", refreshFailed: "refresh failed" },
  // The mechanics (the bearer, one refresh and resend after a 401, the time
  // limit, GET and HEAD retried, never a POST: POST /shop/orders would place
  // the order twice) are kast-common's api-client. This is the wording: the
  // API's text, and "Authentication expired" once the session has ended. An
  // ApiError carries the status, so the query client (`statusOf`) and the
  // pages (`apiErrorStatus`) read it without parsing the message.
  failure: async (response, fallback, authorized) => {
    const text = await response.text();
    if (authorized && response.status === 401 && fallback === "Authentication expired") {
      return new ApiError(fallback, response.status);
    }
    return new ApiError(`API Error ${response.status}: ${text}`, response.status);
  },
});

class ApiClient {
  async request<T = unknown>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const response = await client.optionalRequest(endpoint, {
      ...options,
      headers: { "Content-Type": "application/json", ...options.headers },
    });
    if (response.status === 204) return null as T;
    const ct = response.headers.get("content-type");
    if (ct?.includes("application/json")) return response.json();
    return (await response.text()) as T;
  }

  get<T = unknown>(endpoint: string, params: Record<string, unknown> = {}, signal?: AbortSignal): Promise<T> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) query.append(k, String(v));
    });
    const qs = query.toString();
    const url = qs ? `${endpoint}?${qs}` : endpoint;
    return this.request<T>(url, { method: "GET", signal });
  }

  post<T = unknown>(endpoint: string, data: unknown = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  patch<T = unknown>(endpoint: string, data: unknown = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  delete<T = unknown>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }
}

export const apiClient = new ApiClient();
